import { FILE_UPLOAD_ACCEPT, fileMediaType } from '@exante/gyro-core/file-types';
export { FILE_UPLOAD_DESCRIPTION, fileTypeLabel } from '@exante/gyro-core/file-types';
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const ATTACHMENT_MAX_COUNT = 8;
const PREVIEW_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];
export const ATTACHMENT_ACCEPT = FILE_UPLOAD_ACCEPT;
/** Never render active formats, including HTML and SVG, even if their filenames look safe. */
export function previewMediaType(mediaType) {
    const type = mediaType.toLowerCase().split(';')[0].trim();
    return PREVIEW_TYPES.includes(type) ? type : null;
}
/** MIME labels alone are not enough to put downloaded bytes into a browser viewer. */
export async function verifiedPreviewMedia(blob, mediaType) {
    const type = previewMediaType(mediaType);
    if (!type)
        return null;
    const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
    const starts = (...prefix) => prefix.every((byte, index) => bytes[index] === byte);
    if (type === 'application/pdf' && starts(0x25, 0x50, 0x44, 0x46, 0x2d))
        return type;
    if (type === 'image/png' && starts(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a))
        return type;
    if (type === 'image/jpeg' && starts(0xff, 0xd8, 0xff))
        return type;
    if (type === 'image/gif' && starts(0x47, 0x49, 0x46, 0x38) && (bytes[4] === 0x37 || bytes[4] === 0x39) && bytes[5] === 0x61)
        return type;
    if (type === 'image/webp' && starts(0x52, 0x49, 0x46, 0x46) && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50)
        return type;
    return null;
}
export function attachmentMediaType(file) {
    if (file.size === 0)
        throw new Error('file is empty');
    if (file.size > ATTACHMENT_MAX_BYTES)
        throw new Error('over 5 MB');
    const type = fileMediaType(file.name, file.type);
    if (!type)
        throw new Error(/\.(?:xls|doc|ppt)$/i.test(file.name) ? 'save this older Office file as XLSX, DOCX, or PPTX before attaching it' : 'choose an Office document, PDF, image, Markdown, text/data file, ZIP, or skill package');
    if (previewMediaType(type) && file.type && file.type !== 'application/octet-stream' && file.type.toLowerCase().split(';')[0] !== type)
        throw new Error('choose a file with a matching image or PDF type');
    return type;
}
export async function fileToAttachment(file) {
    const mediaType = attachmentMediaType(file);
    if (previewMediaType(mediaType) && !await verifiedPreviewMedia(file, mediaType))
        throw new Error('file contents do not match its type');
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
    }
    return { kind: mediaType === 'application/pdf' ? 'document' : mediaType.startsWith('image/') && mediaType !== 'image/svg+xml' ? 'image' : 'file', mediaType, data: btoa(binary), name: file.name || 'attachment' };
}
export function artifactFileName(name) {
    return name.split(/[\\/]/).pop()?.replace(/[\u0000-\u001f<>:"|?*]/g, '_').slice(0, 180) || 'download';
}
export function fileSizeLabel(size) {
    if (!Number.isFinite(size) || size < 0)
        return 'File';
    return size < 1024 ? `${size} B` : size < 1024 * 1024 ? `${Math.ceil(size / 1024)} KB` : `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
//# sourceMappingURL=fileMedia.js.map